/**
 * SiteSetup.jsx
 * The part of the Website screen that is read once: which sites are counted,
 * and the one line that does the counting.
 *
 * It sits at the bottom, below the figures, because it is set up once a year
 * and the figures above it are looked at every week.
 *
 * Dependencies: components/Icons.jsx, hooks/useBrands.jsx
 */

import { useState } from 'react';
import { Link, Warning } from '../Icons.jsx';

/**
 * Links that belong to no app.
 *
 * Shown rather than hidden, and with a way to file them, because a download
 * counted under nothing is the one thing this screen cannot explain.
 */
export function Unassigned({ unassigned, sites, busy, onAssign }) {
  if (!unassigned || unassigned.links.length === 0) return null;

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile tile--warn">
          <Warning size={21} />
        </span>
        <div className="card__head-text">
          <h2>Not tied to an app</h2>
          <p>
            {unassigned.links.length === 1 ? 'This download counts' : 'These downloads count'} towards
            no app, so {unassigned.links.length === 1 ? 'it is' : 'they are'} missing from every card
            above. Say which app {unassigned.links.length === 1 ? 'it belongs' : 'they belong'} to.
          </p>
        </div>
      </div>

      <div className="rows">
        {unassigned.links.map((l) => (
          <div className="row" key={l.id}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="row__label">{l.label}</div>
              <div className="row__sub">
                {l.clicks} in this window · goes to {l.url}
              </div>
            </div>
            <div className="row__end">
              <select
                value=""
                disabled={busy}
                onChange={(e) => e.target.value && onAssign(l.id, e.target.value)}
              >
                <option value="">Which app?</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.brand_name || s.host}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SiteSetup({ sites, snippet, brands, busy, onAdd, onToggle, onRemove, onCopy }) {
  const [host, setHost] = useState('');
  const [brandId, setBrandId] = useState('');

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Link size={21} />
        </span>
        <div className="card__head-text">
          <h2>Sites being counted</h2>
          <p>
            One card above for each of these. Only sites listed here are counted, so nobody else can
            fill your figures.
          </p>
        </div>
      </div>

      <div className="rows">
        {sites.length === 0 ? (
          <p className="row__sub">No sites yet. Add one below.</p>
        ) : (
          sites.map((s) => (
            <div className="row" key={s.id}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="row__label">{s.host}</div>
                <div className="row__sub">
                  {s.enabled ? 'Counting' : 'Paused'}
                  {s.brand_name ? ` · shown as ${s.brand_name}` : ''}
                </div>
              </div>
              <div className="row__end" style={{ gap: 12 }}>
                <button className="btn btn--sm" disabled={busy} onClick={() => onToggle(s)}>
                  {s.enabled ? 'Pause' : 'Resume'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  disabled={busy}
                  onClick={() => onRemove(s)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="inline-form">
        <div className="field">
          <label htmlFor="web-host">Website address</label>
          <input
            id="web-host"
            placeholder="peekotype.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="web-site-brand">Which brand</label>
          <select
            id="web-site-brand"
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
          >
            <option value="">No brand</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <span className="hint">Names the card above, and ties the site to the brand’s posts.</span>
        </div>
        <button
          className="btn btn--primary"
          disabled={busy || !host.trim()}
          onClick={async () => {
            await onAdd(host.trim(), brandId || null);
            setHost('');
          }}
        >
          Add site
        </button>
      </div>

      <h3 style={{ marginTop: 24 }}>The one line to paste</h3>
      <p className="row__sub">
        Put this just before <span className="mono">&lt;/body&gt;</span> on every page of each site —
        including the pages with the download and buy buttons on them. It sets no cookie and stores
        nothing about the visitor, so it needs no consent banner.
      </p>
      <p className="mono" style={{ wordBreak: 'break-all' }}>
        {snippet}
      </p>
      <button className="btn btn--sm" onClick={() => onCopy(snippet)}>
        Copy the line
      </button>
    </section>
  );
}
